import { useHeyApi } from "@platform/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { fetchCellReviews } from "./fetchCellReviews";

import type { CellReview } from "@platform/btx-plan-designer";
import type {
  CellReviewFlagType,
  CellReviewResponseRecord,
  ListCellReviewsResponse,
} from "./fetchCellReviews";

export type {
  CellReviewFlagType,
  CellReviewResponseRecord,
} from "./fetchCellReviews";

export interface CellReviewIdentityRequest {
  scope: Record<string, unknown>;
  coverageType: string;
  /** Required for proposalPlan scope; omitted for response scope. */
  benefitPlanId?: string;
  benefitKey: string;
  propertyKey?: string;
  network?: string;
  reviewType: CellReviewFlagType;
}

export interface UseCellReviewStoreOptions {
  rfpId: string | undefined;
  /** React-query cache key; must be unique per tab/scope so caches don't collide. */
  queryKey: readonly unknown[];
  /** Full GET URL (including query string). Query is skipped while undefined. */
  listUrl: string | undefined;
  /**
   * Full PUT/DELETE URL. Mutations are disabled while undefined — callers
   * gate this on whatever they need beyond `rfpId` (e.g. Quotes also
   * requires a selected coverageType).
   */
  mutateUrl: string | undefined;
  enabled?: boolean;
  /** Whether this tab persists the "Edited" (AI-extraction) notation. */
  supportsEdited?: boolean;
  /**
   * Extra cache entries to invalidate once a mutation settles, for stores
   * whose `queryKey` doesn't fully cover a differently-scoped read of the same
   * data — e.g. Quotes' coverageType-scoped list vs. the Needs Review Comments
   * drawer's unscoped one.
   */
  invalidatesQueryKeys?: readonly (readonly unknown[])[];
  /** Derives the client-side `${planId}::${fieldPath}` key for a fetched review record. */
  identityKeyOf: (review: CellReviewResponseRecord) => string;
  /** Builds the PUT/DELETE request identity for a given planId/fieldPath/reviewType. */
  buildIdentity: (
    planId: string,
    fieldPath: string,
    reviewType: CellReviewFlagType,
  ) => CellReviewIdentityRequest;
  onSaveError?: (error: Error) => void;
}

export interface CellReviewStoreResult {
  getReview: (planId: string, fieldPath: string) => CellReview | undefined;
  upsertReview: (
    planId: string,
    fieldPath: string,
    comment: string | null,
  ) => void;
  clearReview: (planId: string, fieldPath: string) => void;
  getBrokerHighlight: (planId: string, fieldPath: string) => boolean;
  setBrokerHighlight: (planId: string, fieldPath: string) => void;
  clearBrokerHighlight: (planId: string, fieldPath: string) => void;
  getEdited: (planId: string, fieldPath: string) => boolean;
  setEdited: (planId: string, fieldPath: string) => void;
  /**
   * The quoted value the analyst cleared the "different than requested" flag at:
   * undefined when the cell was never dismissed, null for a dismissal that
   * stored no value.
   */
  getDismissedDifferenceValue: (
    planId: string,
    fieldPath: string,
  ) => string | null | undefined;
  /** Clears the flag, recording the quoted value it was raised at. */
  dismissDifference: (
    planId: string,
    fieldPath: string,
    differenceValue: string | undefined,
  ) => void;
  isLoading: boolean;
  error: Error | null;
}

// mpx stores a nil UUID for scopes with no benefit plan (e.g. "response").
// Identities for those scopes omit benefitPlanId on the wire and let the
// backend default it; the optimistic cache record mirrors that same value.
const NIL_BENEFIT_PLAN_ID = "00000000-0000-0000-0000-000000000000";

const reviewKey = (planId: string, fieldPath: string): string =>
  `${planId}::${fieldPath}`;

const reviewTypeOf = (review: CellReviewResponseRecord): CellReviewFlagType =>
  review.reviewType ?? "needs_review";

const identityToRecord = (
  identity: CellReviewIdentityRequest,
  comment: string | null,
  dismissedValue?: string,
): CellReviewResponseRecord => ({
  benefitPlanId: identity.benefitPlanId ?? NIL_BENEFIT_PLAN_ID,
  benefitKey: identity.benefitKey,
  coverageType: identity.coverageType,
  propertyKey: identity.propertyKey,
  network: identity.network,
  reviewType: identity.reviewType,
  comment,
  ...(dismissedValue === undefined ? {} : { dismissedValue }),
  updatedAt: new Date().toISOString(),
});

/**
 * Shared data layer behind the Comps/Quotes/Responses cell-review features.
 * Loads every review for the caller's list request in one query and
 * partitions them client-side by reviewType into the methods
 * CellReviewProvider expects. Mutations are reviewType-scoped so
 * "needs_review" and "broker_highlight" (and "edited") coexist on the same
 * cell without clobbering each other.
 *
 * Each tab supplies `identityKeyOf`/`buildIdentity` to translate between its
 * own cell identity (proposal plan + benefit key, or carrier + coverage) and
 * the shared `${planId}::${fieldPath}` client-side key.
 */
export const useCellReviewStore = ({
  rfpId,
  queryKey,
  listUrl,
  mutateUrl,
  enabled = true,
  supportsEdited = false,
  invalidatesQueryKeys,
  identityKeyOf,
  buildIdentity,
  onSaveError,
}: UseCellReviewStoreOptions): CellReviewStoreResult => {
  const { centroPortalClient } = useHeyApi();
  const queryClient = useQueryClient();

  const invalidateSettledQueries = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey });
    invalidatesQueryKeys?.forEach((key) => {
      void queryClient.invalidateQueries({ queryKey: key });
    });
  }, [queryClient, queryKey, invalidatesQueryKeys]);

  const { data, isLoading, error } = useQuery({
    queryKey,
    enabled: enabled && !!rfpId && !!listUrl,
    staleTime: 5 * 60 * 1000,
    queryFn: () => fetchCellReviews(centroPortalClient, listUrl, rfpId),
  });

  const reviews = useMemo(() => data?.reviews ?? [], [data?.reviews]);

  const reviewsByKey = useMemo(
    () =>
      new Map(
        reviews
          .filter((r) => reviewTypeOf(r) === "needs_review")
          .map((r) => [
            identityKeyOf(r),
            {
              id: r.id,
              comment: r.comment,
              createdAt: r.createdAt,
              updatedAt: r.updatedAt,
            },
          ]),
      ),
    [reviews, identityKeyOf],
  );

  const highlightSet = useMemo(
    () =>
      new Set(
        reviews
          .filter((r) => reviewTypeOf(r) === "broker_highlight")
          .map(identityKeyOf),
      ),
    [reviews, identityKeyOf],
  );

  const editedSet = useMemo(
    () =>
      new Set(
        reviews.filter((r) => reviewTypeOf(r) === "edited").map(identityKeyOf),
      ),
    [reviews, identityKeyOf],
  );

  const dismissedDifferenceValues = useMemo(
    () =>
      new Map(
        reviews
          .filter((r) => reviewTypeOf(r) === "difference_dismissed")
          .map((r) => [identityKeyOf(r), r.dismissedValue ?? null] as const),
      ),
    [reviews, identityKeyOf],
  );

  const getReview = useCallback(
    (planId: string, fieldPath: string): CellReview | undefined =>
      reviewsByKey.get(reviewKey(planId, fieldPath)),
    [reviewsByKey],
  );

  const getBrokerHighlight = useCallback(
    (planId: string, fieldPath: string): boolean =>
      highlightSet.has(reviewKey(planId, fieldPath)),
    [highlightSet],
  );

  const getEdited = useCallback(
    (planId: string, fieldPath: string): boolean =>
      editedSet.has(reviewKey(planId, fieldPath)),
    [editedSet],
  );

  const getDismissedDifferenceValue = useCallback(
    (planId: string, fieldPath: string): string | null | undefined =>
      dismissedDifferenceValues.get(reviewKey(planId, fieldPath)),
    [dismissedDifferenceValues],
  );

  const upsertMutation = useMutation({
    mutationFn: async ({
      planId,
      fieldPath,
      reviewType,
      comment,
      dismissedValue,
    }: {
      planId: string;
      fieldPath: string;
      reviewType: CellReviewFlagType;
      comment: string | null;
      dismissedValue?: string;
    }) => {
      if (!mutateUrl) return;

      await centroPortalClient.put<ListCellReviewsResponse, unknown, true>({
        url: mutateUrl,
        body: {
          reviews: [
            {
              ...buildIdentity(planId, fieldPath, reviewType),
              comment,
              ...(dismissedValue === undefined ? {} : { dismissedValue }),
            },
          ],
        },
        responseType: "json",
        throwOnError: true,
      });
    },
    onMutate: async ({
      planId,
      fieldPath,
      reviewType,
      comment,
      dismissedValue,
    }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<ListCellReviewsResponse>(queryKey);
      const key = reviewKey(planId, fieldPath);

      queryClient.setQueryData<ListCellReviewsResponse>(queryKey, (current) => {
        const currentResponse = current ?? { rfpId: rfpId ?? "", reviews: [] };
        const nextReviews = currentResponse.reviews.filter(
          (r) => !(identityKeyOf(r) === key && reviewTypeOf(r) === reviewType),
        );

        nextReviews.push(
          identityToRecord(
            buildIdentity(planId, fieldPath, reviewType),
            comment,
            dismissedValue,
          ),
        );

        return { ...currentResponse, reviews: nextReviews };
      });

      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      if (err instanceof Error) {
        onSaveError?.(err);
      }
    },
    onSettled: invalidateSettledQueries,
  });

  const clearMutation = useMutation({
    mutationFn: async ({
      planId,
      fieldPath,
      reviewType,
    }: {
      planId: string;
      fieldPath: string;
      reviewType: CellReviewFlagType;
    }) => {
      if (!mutateUrl) return;

      await centroPortalClient.delete<void, unknown, true>({
        url: mutateUrl,
        body: { reviews: [buildIdentity(planId, fieldPath, reviewType)] },
        throwOnError: true,
      });
    },
    onMutate: async ({ planId, fieldPath, reviewType }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<ListCellReviewsResponse>(queryKey);
      const key = reviewKey(planId, fieldPath);

      queryClient.setQueryData<ListCellReviewsResponse>(queryKey, (current) => {
        if (!current) return current;
        return {
          ...current,
          reviews: current.reviews.filter(
            (r) =>
              !(identityKeyOf(r) === key && reviewTypeOf(r) === reviewType),
          ),
        };
      });

      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      if (err instanceof Error) {
        onSaveError?.(err);
      }
    },
    onSettled: invalidateSettledQueries,
  });

  // Callers feed these into CellReviewProvider's context value, which the
  // Quotes grid's context menu and cells depend on, so they depend on the
  // mutations' stable `mutate` rather than the mutation results, which are new
  // objects every render.
  const { mutate: upsert } = upsertMutation;
  const { mutate: clear } = clearMutation;

  const upsertReview = useCallback(
    (planId: string, fieldPath: string, comment: string | null) => {
      if (!mutateUrl) return;
      upsert({
        planId,
        fieldPath,
        reviewType: "needs_review",
        comment,
      });
    },
    [mutateUrl, upsert],
  );

  const clearReview = useCallback(
    (planId: string, fieldPath: string) => {
      if (!mutateUrl) return;
      clear({ planId, fieldPath, reviewType: "needs_review" });
    },
    [mutateUrl, clear],
  );

  const setBrokerHighlight = useCallback(
    (planId: string, fieldPath: string) => {
      if (!mutateUrl) return;
      upsert({
        planId,
        fieldPath,
        reviewType: "broker_highlight",
        comment: null,
      });
    },
    [mutateUrl, upsert],
  );

  const clearBrokerHighlight = useCallback(
    (planId: string, fieldPath: string) => {
      if (!mutateUrl) return;
      clear({
        planId,
        fieldPath,
        reviewType: "broker_highlight",
      });
    },
    [mutateUrl, clear],
  );

  const setEdited = useCallback(
    (planId: string, fieldPath: string) => {
      if (!supportsEdited || !mutateUrl) return;
      // Skip the write when the cell is already flagged edited — avoids a
      // redundant PUT on every keystroke-save of an already-edited cell.
      if (editedSet.has(reviewKey(planId, fieldPath))) return;
      upsert({
        planId,
        fieldPath,
        reviewType: "edited",
        comment: null,
      });
    },
    [supportsEdited, mutateUrl, upsert, editedSet],
  );

  const dismissDifference = useCallback(
    (
      planId: string,
      fieldPath: string,
      differenceValue: string | undefined,
    ) => {
      if (!mutateUrl) return;
      // A flag that returned after the quoted value changed is dismissed again
      // at the new value; only a repeat at the stored value is a no-op.
      const storedValue = dismissedDifferenceValues.get(
        reviewKey(planId, fieldPath),
      );
      if (
        storedValue !== undefined &&
        storedValue === (differenceValue ?? null)
      )
        return;
      upsert({
        planId,
        fieldPath,
        reviewType: "difference_dismissed",
        comment: null,
        dismissedValue: differenceValue,
      });
    },
    [mutateUrl, upsert, dismissedDifferenceValues],
  );

  return {
    getReview,
    upsertReview,
    clearReview,
    getBrokerHighlight,
    setBrokerHighlight,
    clearBrokerHighlight,
    getEdited,
    setEdited,
    getDismissedDifferenceValue,
    dismissDifference,
    isLoading,
    error: error instanceof Error ? error : null,
  };
};
